// CB:BEGIN project-cockpit-comprehensive
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useApi } from '@/lib/useApi';
import { AdminAddEmailBidModal } from '@/components/admin/AdminAddEmailBidModal';
import { AddVerifiedContractorModal } from '@/components/admin/AddVerifiedContractorModal';
import { P0ProjectFlow } from '@/components/P0ProjectFlow'; // CB:BEGIN p0-phase-1.3-6.3-admin
import { NextActionBanner } from '@/components/NextActionBanner'; // CB:BEGIN next-action-banner
import { 
  FaUpload, 
  FaCheck, 
  FaTimes, 
  FaDollarSign, 
  FaFileAlt, 
  FaDownload,
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaArrowLeft
} from 'react-icons/fa';

interface Project {
  id: string;
  address: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  homeownerPlaceholder?: boolean;
  homeowner?: {
    firstName?: string;
    lastName?: string;
    user: { 
      email: string; 
      phone?: string;
    };
  };
  bids: Array<{
    id: string;
    amount: number;
    status: string;
    title?: string;
    scopeNotes?: string;
    warrantyLaborYears?: number;
    warrantyMaterialYears?: number;
    warrantyTransferable?: boolean;
    warrantyNotes?: string;
    contractor?: {
      company?: string;
      user: { email: string; firstName?: string; lastName?: string };
    };
  }>;
  manualBids: Array<{
    id: string;
    contractorName: string;
    contractorEmail?: string;
    contractorPhone?: string;
    licenseNumber?: string;
    priceCents: number;
    paymentTerms: string;
    timeline: string;
    startDate?: string;
    notes?: string;
    attachmentUrl?: string;
    isWinner: boolean;
    createdAt: string;
  }>;
  phases: Array<{
    id: string;
    number: number;
    title?: string;
    status: string;
    percentOfTotal?: number;
    amountCents?: number;
    contractorSubmittedAt?: string;
    homeownerAcceptedAt?: string;
    depositReleasedAt?: string;
    completedAt?: string;
  }>;
  documents: Array<{
    id: string;
    type: string;
    s3Key: string;
    uploadedBy: string;
    createdAt: string;
  }>;
  escrowAccount?: {
    id: string;
    targetCents: number;
    balanceCents: number;
    ledger: any[];
  };
  signing?: {
    homeownerSigned: boolean;
    contractorSigned: boolean;
    fullySigned: boolean;
    homeownerSignedAt?: string;
    contractorSignedAt?: string;
  };
  escrow?: {
    id: string;
    status: string;
    totalAmountCents: number;
    bidAmountCents?: number;
    platformFeeCents?: number;
    stripePaymentIntentId?: string;
    stripePaymentIntentStatus?: string;
    releasedCents: number;
    frozenAt?: string;
    milestones: any[];
  };
}

interface ProjectCockpitProps {
  onNavigate?: (screen: string) => void;
  projectId?: string;
}

// CB:BEGIN admin-quick-actions-component
// AdminQuickActions removed — functionality consolidated into Admin Controls tab
// CB:END admin-quick-actions-component

const ProjectCockpit: React.FC<ProjectCockpitProps> = ({ onNavigate, projectId: propProjectId }) => {
  const { id: urlProjectId } = useParams<{ id: string }>();
  const projectId = propProjectId || urlProjectId;
  const { user } = useUser();
  const api = useApi();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAddBidModal, setShowAddBidModal] = useState(false);
  const [showAddVerifiedContractorModal, setShowAddVerifiedContractorModal] = useState(false);
  const [verifiedContractors, setVerifiedContractors] = useState<string[]>([]);
  const [isBidFormValid, setIsBidFormValid] = useState(false);
  const [submitBidTrigger, setSubmitBidTrigger] = useState(0);
  const [auditEntries, setAuditEntries] = useState<{ id: string; action: string; actorUserId?: string; entityType: string; entityId: string; payload?: unknown; createdAt: string }[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [contractEvents, setContractEvents] = useState<any[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [stripeDiag, setStripeDiag] = useState<string | null>(null);

  // CB:BEGIN admin-overrides-state
  const [reminderNote, setReminderNote] = useState('');
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderResult, setReminderResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [transitionStatus, setTransitionStatus] = useState('');
  const [transitionJustification, setTransitionJustification] = useState('');
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [transitionResult, setTransitionResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [dangerLoading, setDangerLoading] = useState<string | null>(null);
  const [dangerResult, setDangerResult] = useState<{ ok: boolean; msg: string } | null>(null);
  // CB:END admin-overrides-state

  // CB:BEGIN admin-cockpit-id-fix
  useEffect(() => {
    if (projectId) {
      fetchProject();
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId && activeTab === 'contract') {
      setContractsLoading(true);
      api.get(`/api/admin/projects/${projectId}/contracts`)
        .then((data: any) => setContractEvents(data?.contracts ?? []))
        .catch(() => setContractEvents([]))
        .finally(() => setContractsLoading(false));
    }
  }, [projectId, activeTab]);

  useEffect(() => {
    if (projectId && activeTab === 'audit') {
      setAuditLoading(true);
      api.get(`/api/admin/projects/${projectId}/audit`).then((data: { entries?: typeof auditEntries }) => {
        setAuditEntries(data?.entries ?? []);
      }).catch(() => setAuditEntries([])).finally(() => setAuditLoading(false));
    }
  }, [projectId, activeTab]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/api/admin/projects/${projectId}`);
      setProject(data);
      // CB:BEGIN load-verified-contractors
      // Load verified contractors from project metadata
      if (data.metadata && typeof data.metadata === 'object' && Array.isArray(data.metadata.verifiedContractors)) {
        setVerifiedContractors(data.metadata.verifiedContractors);
      } else {
        setVerifiedContractors([]);
      }
      // CB:END load-verified-contractors
    } catch (err) {
      console.error('❌ Error fetching project:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch project');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action: string, data?: any) => {
    try {
      setActionLoading(action);
      await api.post(`/api/admin/projects/${projectId}/${action}`, data);
      await fetchProject(); // Refresh project data
    } catch (err) {
      console.error(`❌ Error ${action}:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };
  // CB:END admin-cockpit-id-fix

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'REPORT_REQUESTED': 'bg-yellow-100 text-yellow-800',
      'REPORT_UPLOADED': 'bg-blue-100 text-blue-800',
      'BIDS_READY': 'bg-green-100 text-green-800',
      'DECIDING': 'bg-purple-100 text-purple-800',
      'ESCROW_PENDING': 'bg-indigo-100 text-indigo-800',
      'ACTIVE_P1': 'bg-orange-100 text-orange-800',
      'ACTIVE_P2': 'bg-orange-100 text-orange-800',
      'ACTIVE_P3': 'bg-orange-100 text-orange-800',
      'CLOSED': 'bg-green-100 text-green-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Project not found</h3>
        <p className="mt-2 text-sm text-gray-500">The project you're looking for doesn't exist.</p>
        <button
          onClick={() => onNavigate?.('admin-projects-list')}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          Back to Projects
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'bids', name: 'Bids' },
    { id: 'contract', name: 'Contract' },
    { id: 'escrow', name: 'Escrow' },
    { id: 'milestones', name: 'Milestones' },
    { id: 'documents', name: 'Documents' },
    { id: 'audit', name: 'Audit Log' },
    { id: 'admin-overrides', name: 'Admin Controls' }
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* CB:BEGIN modern-admin-header */}
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <button
              onClick={() => onNavigate?.('admin-projects-list')}
              style={{
                marginRight: '16px',
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                background: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.color = '#111827';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              <FaArrowLeft style={{ width: '20px', height: '20px' }} />
            </button>
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: '32px', fontWeight: '700', margin: '0 0 8px 0', color: '#111827' }}>
                Project Cockpit
              </h1>
              <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 0 12px 0' }}>{project.address}</p>
              <div>
                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                  {project.status.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>
          {/* CB:BEGIN add-verified-contractor-button */}
          {activeTab === 'bids' && (
            <button
              onClick={() => setShowAddVerifiedContractorModal(true)}
              style={{
                background: '#2563eb',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb'; }}
            >
              <FaPlus />
              Add Verified Contractor
            </button>
          )}
          {/* CB:END add-verified-contractor-button */}
        </div>
      </div>
      {/* CB:END modern-admin-header */}

      {/* CB:BEGIN admin-ops-off-platform-banner */}
      {project.homeownerPlaceholder && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '12px 16px', marginBottom: '24px' }}>
          <strong>OFF-PLATFORM HOMEOWNER</strong> — Admin is acting as proxy. All actions are audited. Contractor cannot see homeowner contact.
        </div>
      )}
      {/* CB:END admin-ops-off-platform-banner */}

      {/* CB:BEGIN modern-error-message */}
      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'start'
        }}>
          <div style={{ flexShrink: 0, marginRight: '12px' }}>
            <svg style={{ width: '20px', height: '20px', color: '#ef4444' }} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b', margin: '0 0 4px 0' }}>Error</h3>
            <p style={{ fontSize: '14px', color: '#b91c1c', margin: 0 }}>{error}</p>
          </div>
        </div>
      )}
      {/* CB:END modern-error-message */}

      {/* CB:BEGIN modern-tabs */}
      {/* Tabs */}
      <div className="dashboard-card" style={{ marginBottom: '24px', padding: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          background: 'white'
        }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '16px 24px',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #2563eb' : '3px solid transparent',
                color: activeTab === tab.id ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === tab.id ? '600' : '400',
                cursor: 'pointer',
                fontSize: '15px',
                transition: 'all 0.2s',
                background: activeTab === tab.id ? '#eff6ff' : 'transparent'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.color = '#374151';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }
              }}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>
      {/* CB:END modern-tabs */}

      {/* CB:BEGIN modern-tab-content */}
      {/* Tab Content */}
      <div className="dashboard-card">
        {activeTab === 'overview' && (
          <div style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Project Overview</h3>


            {/* Admin controls moved to Admin Controls tab */}

            <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Project ID</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0, fontFamily: 'monospace' }}>
                  {project.id}
                </dd>
              </div>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Homeowner</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
                  {project.homeowner ? 
                    `${project.homeowner.firstName || ''} ${project.homeowner.lastName || ''}`.trim() || 'Unnamed' :
                    'Not assigned'
                  }
                </dd>
              </div>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Email</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
                  {project.homeowner?.user.email || 'N/A'}
                </dd>
              </div>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Phone</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
                  {project.homeowner?.user.phone || 'N/A'}
                </dd>
              </div>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Status</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>{project.status}</dd>
              </div>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Created</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>{formatDate(project.createdAt)}</dd>
              </div>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Last Updated</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>{formatDate(project.updatedAt)}</dd>
              </div>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Total Bids</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>{(project.bids?.length || 0) + (project.manualBids?.length || 0)}</dd>
              </div>
              <div style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <dt style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>Phases</dt>
                <dd style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>{project.phases?.length || 0}</dd>
              </div>
            </dl>
            {/* CB:BEGIN p0-phase-1.3-6.3-admin — Escrow, gate, pause, release history, override */}
            {projectId && project.escrow && (
              <div style={{ marginTop: 32, padding: 16, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>P0: Escrow, gate &amp; start</h4>
                <P0ProjectFlow projectId={projectId} role="admin" onUpdated={fetchProject} />
              </div>
            )}
            {/* CB:END p0-phase-1.3-6.3-admin */}
          </div>
        )}

        {activeTab === 'bids' && (
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>Bids Management</h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                {/* CB:BEGIN add-email-bid-button */}
                <button
                  onClick={() => setShowAddBidModal(true)}
                  style={{
                    background: '#2563eb',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb'; }}
                >
                  <FaPlus />
                  Add Bid
                </button>
                {/* CB:END add-email-bid-button */}
                <button
                  onClick={() => {
                    if (isBidFormValid && showAddBidModal) {
                      setSubmitBidTrigger(prev => prev + 1);
                    }
                  }}
                  disabled={!isBidFormValid || !showAddBidModal}
                  style={{
                    background: isBidFormValid ? '#10b981' : '#9ca3af',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: isBidFormValid ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (isBidFormValid) {
                      e.currentTarget.style.background = '#059669';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isBidFormValid) {
                      e.currentTarget.style.background = '#10b981';
                    } else {
                      e.currentTarget.style.background = '#9ca3af';
                    }
                  }}
                >
                  Submit Bid
                </button>
              </div>
            </div>
            {/* CB:BEGIN display-regular-bids */}
            {project.bids && project.bids.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px' }}>Contractor Portal Bids</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {project.bids.map((bid) => {
                    // Try to parse the note field as a structured proposal
                    let proposal: any = null;
                    const rawNote = (bid as any).note || (bid as any).notes || (bid as any).scopeNotes;
                    if (rawNote) {
                      try { proposal = JSON.parse(rawNote); } catch {}
                    }
                    const totalCents = bid.amount || (proposal?.lineItems || []).reduce((s: number, li: any) => s + (li.totalCents || 0), 0);

                    return (
                      <div key={bid.id} style={{
                        border: (bid as any).isAccepted ? '2px solid #2563eb' : '1px solid #e5e7eb',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        background: 'white',
                      }}>
                        {/* Bid header */}
                        <div style={{ background: (bid as any).isAccepted ? '#eff6ff' : '#f8fafc', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                              {bid.contractor?.company || bid.contractor?.user?.email || 'Contractor'}
                            </span>
                            {bid.status && (
                              <span style={{
                                fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '9999px',
                                background: bid.status === 'PUBLISHED' ? '#d1fae5' : bid.status === 'PENDING' ? '#fef3c7' : '#f3f4f6',
                                color: bid.status === 'PUBLISHED' ? '#065f46' : bid.status === 'PENDING' ? '#92400e' : '#374151',
                              }}>{bid.status}</span>
                            )}
                            {(bid as any).isAccepted && (
                              <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '9999px', background: '#dbeafe', color: '#1e40af' }}>✓ Accepted</span>
                            )}
                          </div>
                          <div style={{ fontSize: '22px', fontWeight: '800', color: '#111827' }}>
                            ${(totalCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>

                        {proposal ? (
                          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {/* Dates row */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
                              {[
                                { label: 'Issue Date', val: proposal.issueDate },
                                { label: 'Expires', val: proposal.expirationDate },
                                { label: 'Earliest Start', val: proposal.earliestStartDate },
                                { label: 'Est. Completion', val: proposal.estimatedCompletionDate },
                                { label: 'Duration', val: proposal.estimatedDurationDays ? `${proposal.estimatedDurationDays} day${proposal.estimatedDurationDays !== 1 ? 's' : ''}` : null },
                              ].filter(d => d.val).map(d => (
                                <div key={d.label} style={{ background: '#f9fafb', borderRadius: '7px', padding: '10px 12px' }}>
                                  <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>{d.label}</div>
                                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{d.val}</div>
                                </div>
                              ))}
                            </div>

                            {/* Line items */}
                            {proposal.lineItems?.length > 0 && (
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Line Items</div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                  <thead>
                                    <tr style={{ background: '#f1f5f9' }}>
                                      {['Category', 'Description', 'Qty', 'Unit', 'Total'].map(h => (
                                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '12px' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {proposal.lineItems.filter((li: any) => li.description || li.totalCents).map((li: any, i: number) => (
                                      <tr key={li.id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '7px 10px', color: '#6b7280' }}>{li.category || '—'}</td>
                                        <td style={{ padding: '7px 10px', color: '#111827' }}>{li.description || '—'}</td>
                                        <td style={{ padding: '7px 10px', color: '#6b7280' }}>{li.quantity ?? '—'}</td>
                                        <td style={{ padding: '7px 10px', color: '#6b7280' }}>{li.unit || '—'}</td>
                                        <td style={{ padding: '7px 10px', fontWeight: '600', color: '#111827' }}>
                                          {li.totalCents != null ? `$${(li.totalCents / 100).toFixed(2)}` : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr style={{ background: '#f8fafc' }}>
                                      <td colSpan={4} style={{ padding: '8px 10px', fontWeight: '700', color: '#374151', fontSize: '13px' }}>Total</td>
                                      <td style={{ padding: '8px 10px', fontWeight: '800', color: '#111827', fontSize: '14px' }}>
                                        ${(totalCents / 100).toFixed(2)}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            )}

                            {/* Roof + Material specs */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                              {/* Roof specs */}
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Roof Specs</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {[
                                    { label: 'Type', val: proposal.roofType },
                                    { label: 'Size', val: proposal.roofSizeSqFt ? `${proposal.roofSizeSqFt.toLocaleString()} sq ft (${proposal.roofSizeSquares} squares)` : null },
                                    { label: 'Pitch', val: proposal.roofPitch },
                                    { label: 'Stories', val: proposal.stories },
                                    { label: 'Waste Factor', val: proposal.wasteFactorPct ? `${proposal.wasteFactorPct}%` : null },
                                  ].filter(r => r.val != null).map(r => (
                                    <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
                                      <span style={{ fontSize: '13px', color: '#6b7280' }}>{r.label}</span>
                                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{r.val}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Material specs */}
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Material</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {[
                                    { label: 'Type', val: proposal.materialType },
                                    { label: 'Manufacturer', val: proposal.materialManufacturer },
                                    { label: 'Product', val: proposal.materialProductLine },
                                  ].filter(r => r.val).map(r => (
                                    <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
                                      <span style={{ fontSize: '13px', color: '#6b7280' }}>{r.label}</span>
                                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{r.val}</span>
                                    </div>
                                  ))}
                                  {proposal.materialSpecUrl && (
                                    <a href={proposal.materialSpecUrl.startsWith('http') ? proposal.materialSpecUrl : `https://${proposal.materialSpecUrl}`}
                                      target="_blank" rel="noopener noreferrer"
                                      style={{ fontSize: '13px', color: '#2563eb', marginTop: '4px', display: 'inline-block' }}>
                                      View Spec Sheet ↗
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Colors + Warranty + Contractor info */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                              {proposal.availableColors?.length > 0 && (
                                <div>
                                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Available Colors</div>
                                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    {proposal.availableColors.filter((c: any) => c.available !== false).map((c: any) => (
                                      <span key={c.name} style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '9999px', background: '#e0e7ff', color: '#3730a3', fontWeight: '600' }}>{c.name}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {(proposal.warrantyLaborYears || proposal.warrantyMaterialYears) && (
                                <div>
                                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Warranty</div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {proposal.warrantyLaborYears && <span style={{ fontSize: '13px', color: '#374151' }}>Labor: <strong>{proposal.warrantyLaborYears} yr</strong></span>}
                                    {proposal.warrantyMaterialYears && <span style={{ fontSize: '13px', color: '#374151' }}>Material: <strong>{proposal.warrantyMaterialYears} yr</strong></span>}
                                  </div>
                                </div>
                              )}
                              {(proposal.contractorLicense || proposal.contractorInsurance) && (
                                <div>
                                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Credentials</div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {proposal.contractorLicense && <span style={{ fontSize: '13px', color: '#374151' }}>License: <strong>{proposal.contractorLicense}</strong></span>}
                                    {proposal.contractorInsurance && <span style={{ fontSize: '13px', color: '#374151' }}>Insurance: <strong>{proposal.contractorInsurance}</strong></span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : rawNote ? (
                          <div style={{ padding: '16px 20px' }}>
                            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0, whiteSpace: 'pre-wrap' }}>{rawNote}</p>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* CB:END display-regular-bids */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {(project.manualBids || []).map((bid, index) => (
                <div key={bid.id} className="dashboard-card" style={{
                  padding: '20px',
                  border: bid.isWinner ? '2px solid #10b981' : '1px solid #e5e7eb',
                  background: bid.isWinner ? '#f0fdf4' : 'white'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Bid #{index + 1}</h4>
                        {bid.isWinner && (
                          <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Winner
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '16px' }}>
                        <div>
                          <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>{bid.contractorName}</p>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0' }}>{bid.contractorEmail}</p>
                          {bid.contractorPhone && <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0' }}>{bid.contractorPhone}</p>}
                        </div>
                        <div>
                          <p style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>{formatCurrency(bid.priceCents)}</p>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0' }}>{bid.timeline}</p>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0' }}>{bid.paymentTerms}</p>
                        </div>
                      </div>
                      {bid.notes && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                          <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>{bid.notes}</p>
                        </div>
                      )}
                      {bid.licenseNumber && (
                        <div style={{ marginTop: '12px' }}>
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>License: <strong>{bid.licenseNumber}</strong></p>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                      {!bid.isWinner && (
                        <button
                          onClick={() => handleAction(`bids/${bid.id}/choose`)}
                          disabled={actionLoading === `bids/${bid.id}/choose`}
                          style={{
                            background: '#10b981',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '600',
                            border: 'none',
                            cursor: actionLoading === `bids/${bid.id}/choose` ? 'not-allowed' : 'pointer',
                            opacity: actionLoading === `bids/${bid.id}/choose` ? 0.7 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (actionLoading !== `bids/${bid.id}/choose`) {
                              e.currentTarget.style.background = '#059669';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (actionLoading !== `bids/${bid.id}/choose`) {
                              e.currentTarget.style.background = '#10b981';
                            }
                          }}
                        >
                          {actionLoading === `bids/${bid.id}/choose` ? 'Choosing...' : 'Choose'}
                        </button>
                      )}
                      {bid.attachmentUrl && (
                        <a
                          href={bid.attachmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '8px',
                            borderRadius: '6px',
                            background: '#eff6ff',
                            color: '#2563eb',
                            textDecoration: 'none',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#dbeafe';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#eff6ff';
                          }}
                        >
                          <FaDownload style={{ width: '16px', height: '16px' }} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'contract' && (
          <div style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Contracts &amp; Signatures</h3>

            {/* Signing summary from project */}
            {project.signing && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                {/* Homeowner agreement */}
                <div style={{
                  border: project.signing.homeownerSigned ? '2px solid #10b981' : '1px solid #e5e7eb',
                  borderRadius: '10px', padding: '20px',
                  background: project.signing.homeownerSigned ? '#f0fdf4' : '#fafafa',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '20px' }}>{project.signing.homeownerSigned ? '✅' : '⏳'}</span>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>Homeowner Agreement</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Platform service & escrow terms</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#6b7280' }}>Status</span>
                      <span style={{ fontWeight: '700', color: project.signing.homeownerSigned ? '#059669' : '#d97706' }}>
                        {project.signing.homeownerSigned ? 'Signed' : 'Pending'}
                      </span>
                    </div>
                    {project.signing.homeownerSignedAt && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#6b7280' }}>Signed at</span>
                        <span style={{ color: '#111827' }}>{formatDate(project.signing.homeownerSignedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contractor agreement */}
                <div style={{
                  border: project.signing.contractorSigned ? '2px solid #10b981' : '1px solid #e5e7eb',
                  borderRadius: '10px', padding: '20px',
                  background: project.signing.contractorSigned ? '#f0fdf4' : '#fafafa',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '20px' }}>{project.signing.contractorSigned ? '✅' : '⏳'}</span>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827' }}>Contractor Agreement</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Project execution & payout terms</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: '#6b7280' }}>Status</span>
                      <span style={{ fontWeight: '700', color: project.signing.contractorSigned ? '#059669' : '#d97706' }}>
                        {project.signing.contractorSigned ? 'Signed' : 'Pending'}
                      </span>
                    </div>
                    {project.signing.contractorSignedAt && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: '#6b7280' }}>Signed at</span>
                        <span style={{ color: '#111827' }}>{formatDate(project.signing.contractorSignedAt)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contract signing events detail */}
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
              Signing History
            </div>
            {contractsLoading ? (
              <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading…</p>
            ) : contractEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 24px', background: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '15px', fontWeight: '600', color: '#374151', margin: '0 0 4px' }}>No signing events yet</p>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>Contracts appear here after homeowner or contractor signs.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {contractEvents.map((ev: any) => (
                  <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '0 0 auto' }}>
                      <span style={{
                        fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '9999px',
                        background: ev.party === 'HOMEOWNER' ? '#dbeafe' : '#d1fae5',
                        color: ev.party === 'HOMEOWNER' ? '#1e40af' : '#065f46',
                      }}>{ev.party}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                        {ev.party === 'HOMEOWNER' ? 'Homeowner Agreement' : 'Contractor Project Agreement'}
                        {ev.signingStatus === 'REPLACED' && <span style={{ fontSize: '11px', color: '#dc2626', marginLeft: '8px' }}>(Replaced)</span>}
                        {ev.signingStatus === 'INVALIDATED' && <span style={{ fontSize: '11px', color: '#dc2626', marginLeft: '8px' }}>(Invalidated)</span>}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        v{ev.documentVersion} · {ev.signatureMethod ?? 'digital'}
                        {ev.source && ` · ${ev.source}`}
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'right' }}>
                      {ev.signedAt ? formatDate(ev.signedAt) : ev.uploadedAt ? `Uploaded ${formatDate(ev.uploadedAt)}` : '—'}
                    </div>
                    {ev.documentPdfKey && (
                      <a href={`/api/admin/projects/${projectId}/contracts/${ev.id}/download`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        View PDF ↗
                      </a>
                    )}
                    {ev.uploadFileKey && (
                      <a href={`/api/admin/projects/${projectId}/contracts/${ev.id}/download`}
                        target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        View Upload ↗
                      </a>
                    )}
                    {ev.signingNotes && (
                      <div style={{ width: '100%', fontSize: '12px', color: '#6b7280', marginTop: '4px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                        Notes: {ev.signingNotes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>Documents</h3>
              <input
                type="file"
                id="document-upload"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file && projectId) {
                    try {
                      // Get presigned URL
                      const presignRes = await fetch(`/api/admin/projects/${projectId}/documents/presign`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ fileName: file.name, fileType: file.type })
                      });
                      const { uploadUrl, fileKey } = await presignRes.json();
                      
                      // Upload to S3
                      await fetch(uploadUrl, {
                        method: 'PUT',
                        body: file,
                        headers: { 'Content-Type': file.type }
                      });
                      
                      // Create document record
                      await fetch(`/api/admin/projects/${projectId}/documents`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          fileName: file.name,
                          fileType: file.type,
                          s3Key: fileKey
                        })
                      });
                      
                      // Reload project data
                      window.location.reload();
                    } catch (err) {
                      console.error('Error uploading document:', err);
                      alert('Failed to upload document');
                    }
                  }
                }}
              />
              <label
                htmlFor="document-upload"
                style={{
                  background: '#2563eb',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#1d4ed8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#2563eb'; }}
              >
                <FaPlus />
                Upload Document
              </label>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {(project.documents || []).map((doc) => (
                <div key={doc.id} className="dashboard-card" style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FaFileAlt style={{ width: '24px', height: '24px', color: 'white' }} />
                    </div>
                    <div>
                      <p style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '0 0 4px 0' }}>{doc.type}</p>
                      <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                        Uploaded {formatDate(doc.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={{
                      padding: '8px',
                      borderRadius: '6px',
                      background: '#eff6ff',
                      color: '#2563eb',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
                    >
                      <FaEye style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button style={{
                      padding: '8px',
                      borderRadius: '6px',
                      background: '#eff6ff',
                      color: '#2563eb',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; }}
                    >
                      <FaDownload style={{ width: '16px', height: '16px' }} />
                    </button>
                    <button style={{
                      padding: '8px',
                      borderRadius: '6px',
                      background: '#fef2f2',
                      color: '#dc2626',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                    >
                      <FaTrash style={{ width: '16px', height: '16px' }} />
                    </button>
                  </div>
                </div>
              ))}
              {(!project.documents || project.documents.length === 0) && (
                <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                  <FaFileAlt style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>No documents</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Upload documents to get started.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'escrow' && (
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '8px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0 }}>Escrow Management</h3>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {stripeDiag && (
                  <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0' }}>
                    {stripeDiag}
                  </span>
                )}
                {(project.escrow as any)?.stripePaymentIntentId && (
                  <button
                    onClick={async () => {
                      try {
                        setActionLoading('check-pi');
                        setStripeDiag(null);
                        const piId = (project.escrow as any).stripePaymentIntentId;
                        const data = await api.get(`/api/admin/payments/pi-lookup/${piId}`);
                        const frontendKeyMode = (window as any).__VITE_STRIPE_PUBLISHABLE_KEY?.startsWith?.('pk_live_') ? 'live' : 'test';
                        if (data.found) {
                          const modeMismatch = data.livemode !== (frontendKeyMode === 'live');
                          const modeNote = modeMismatch ? ' ⚠️ MODE MISMATCH: backend is ' + (data.livemode ? 'LIVE' : 'TEST') + ' but frontend key is ' + frontendKeyMode : '';
                          setStripeDiag(`✅ PI found in backend Stripe — status: ${data.status}, livemode: ${data.livemode}${modeNote}. If frontend still fails, check that STRIPE_SECRET_KEY (Railway) and VITE_STRIPE_PUBLISHABLE_KEY (Vercel) are from the SAME Stripe account.`);
                        } else {
                          setStripeDiag(`❌ PI NOT found in backend Stripe account: ${data.error}. This confirms a Stripe key mismatch — STRIPE_SECRET_KEY in Railway is from a different account than VITE_STRIPE_PUBLISHABLE_KEY in Vercel.`);
                        }
                      } catch (err: any) {
                        setStripeDiag(`❌ Lookup failed: ${err?.message}`);
                      } finally {
                        setActionLoading(null);
                      }
                    }}
                    disabled={actionLoading === 'check-pi'}
                    style={{
                      background: actionLoading === 'check-pi' ? '#9ca3af' : '#6366f1',
                      color: 'white',
                      padding: '8px 14px',
                      borderRadius: '7px',
                      fontSize: '13px',
                      fontWeight: '600',
                      border: 'none',
                      cursor: actionLoading === 'check-pi' ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {actionLoading === 'check-pi' ? 'Checking…' : 'Check PI in Stripe'}
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (!confirm(`Clear stale payment intent for project ${projectId}? The homeowner's next payment attempt will create a fresh Stripe PI.`)) return;
                    try {
                      setActionLoading('clear-pi');
                      setStripeDiag(null);
                      await api.post('/api/admin/payments/clear-payment-intent', { projectId });
                      alert('✅ Stale payment intent cleared. Homeowner can now retry payment.');
                      await fetchProject();
                    } catch (err: any) {
                      alert(`❌ ${err?.message || 'Failed to clear payment intent'}`);
                    } finally {
                      setActionLoading(null);
                    }
                  }}
                  disabled={actionLoading === 'clear-pi'}
                  style={{
                    background: actionLoading === 'clear-pi' ? '#9ca3af' : '#dc2626',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: '7px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: actionLoading === 'clear-pi' ? 'not-allowed' : 'pointer',
                  }}
                >
                  {actionLoading === 'clear-pi' ? 'Clearing…' : 'Clear Stale PI'}
                </button>
              </div>
            </div>
            {project.escrow ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Status + Stripe PI info */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                  <div className="dashboard-card" style={{ padding: '20px', textAlign: 'center' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Status</h4>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>{(project.escrow as any).status}</p>
                  </div>
                  <div className="dashboard-card" style={{ padding: '20px', textAlign: 'center' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Total (Bid + Fee)</h4>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>{formatCurrency((project.escrow as any).totalAmountCents ?? 0)}</p>
                  </div>
                  <div className="dashboard-card" style={{ padding: '20px', textAlign: 'center' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Bid Amount</h4>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 }}>{formatCurrency((project.escrow as any).bidAmountCents ?? 0)}</p>
                  </div>
                  <div className="dashboard-card" style={{ padding: '20px', textAlign: 'center' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Platform Fee</h4>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#6b7280', margin: 0 }}>{formatCurrency((project.escrow as any).platformFeeCents ?? 0)}</p>
                  </div>
                  <div className="dashboard-card" style={{ padding: '20px', textAlign: 'center' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280', margin: '0 0 8px 0' }}>Released</h4>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#10b981', margin: 0 }}>{formatCurrency((project.escrow as any).releasedCents ?? 0)}</p>
                  </div>
                </div>
                {/* Stripe PI details */}
                {(project.escrow as any).stripePaymentIntentId && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Stripe Payment Intent</div>
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                      <div>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>ID</span>
                        <p style={{ fontSize: 13, fontFamily: 'monospace', color: '#111827', margin: '2px 0 0' }}>{(project.escrow as any).stripePaymentIntentId}</p>
                      </div>
                      <div>
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Status</span>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#111827', margin: '2px 0 0' }}>{(project.escrow as any).stripePaymentIntentStatus ?? 'unknown'}</p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Milestones breakdown */}
                {(project.escrow as any).milestones?.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' }}>Milestones</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {((project.escrow as any).milestones || []).map((m: any) => (
                        <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                          <span style={{ fontSize: 14, color: '#374151' }}>{m.label ?? m.milestoneKey}</span>
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{formatCurrency(m.amountCents ?? 0)}</span>
                            <span style={{ fontSize: 12, color: m.status === 'RELEASED' ? '#059669' : '#6b7280' }}>{m.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                <FaDollarSign style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>No escrow found</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Escrow is set up when a bid is accepted.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'milestones' && (
          <div style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '24px' }}>Project Milestones</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {(project.phases || []).map((phase) => {
                // CB:BEGIN milestone-status-display
                const isPending = phase.status === 'PENDING';
                const isContractorSubmitted = phase.status === 'CONTRACTOR_SUBMITTED';
                const isHomeownerApproved = phase.status === 'HOMEOWNER_APPROVED';
                const isReleased = phase.status === 'ADMIN_RELEASED';
                
                // Get status display text
                const getStatusText = () => {
                  if (isReleased) return 'Payment Released';
                  if (isHomeownerApproved) return 'Homeowner Approved';
                  if (isContractorSubmitted) return 'Contractor Submitted';
                  return 'Pending';
                };
                
                // Get status badge color
                const getStatusBadgeClass = () => {
                  if (isReleased) return 'bg-gray-100 text-gray-600';
                  if (isHomeownerApproved) return 'bg-green-100 text-green-800';
                  if (isContractorSubmitted) return 'bg-blue-100 text-blue-800';
                  return 'bg-yellow-100 text-yellow-800';
                };
                // CB:END milestone-status-display
                
                return (
                  <div key={phase.id} className="dashboard-card" style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>Phase {phase.number}</h4>
                        {(phase as any).name && <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0' }}>{(phase as any).name}</p>}
                        {(phase as any).percent != null && (
                          <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0' }}>{(phase as any).percent}% of total</p>
                        )}
                        {phase.amountCents != null && (
                          <p style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '8px 0 0 0' }}>{formatCurrency(phase.amountCents)}</p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass()}`}>
                          {getStatusText()}
                        </span>
                        {(phase as any).submittedAt && (
                          <p style={{ fontSize: '12px', color: '#6b7280', margin: '8px 0 0 0' }}>
                            Contractor submitted: {formatDate((phase as any).submittedAt)}
                          </p>
                        )}
                        {(phase as any).approvedAt && (
                          <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
                            Homeowner approved: {formatDate((phase as any).approvedAt)}
                          </p>
                        )}
                        {(phase as any).releasedAt && (
                          <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>
                            Released: {formatDate((phase as any).releasedAt)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* CB:BEGIN release-payment-button */}
                    {/* Release Payment Button - Only show when homeowner has approved */}
                    {isHomeownerApproved && !isReleased && (
                      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={async () => {
                            if (!confirm(`Release payment of ${formatCurrency(phase.amountCents || 0)} to contractor's Stripe account?`)) return;
                            
                            setActionLoading(`release-${phase.number}`);
                            try {
                              await api.post(`/api/projects/${projectId}/phases/${phase.number}/release`);
                              await fetchProject();
                              alert('Payment released successfully! The funds have been transferred to the contractor\'s Stripe account.');
                            } catch (err: any) {
                              console.error('Error releasing payment:', err);
                              const errorMessage = err?.response?.data?.error || err?.message || 'Failed to release payment';
                              alert(`Failed to release payment: ${errorMessage}`);
                            } finally {
                              setActionLoading(null);
                            }
                          }}
                          disabled={actionLoading === `release-${phase.number}`}
                          style={{
                            background: actionLoading === `release-${phase.number}` ? '#9ca3af' : '#10b981',
                            color: 'white',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            border: 'none',
                            cursor: actionLoading === `release-${phase.number}` ? 'not-allowed' : 'pointer',
                            opacity: actionLoading === `release-${phase.number}` ? 0.7 : 1,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            if (actionLoading !== `release-${phase.number}`) {
                              e.currentTarget.style.background = '#059669';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (actionLoading !== `release-${phase.number}`) {
                              e.currentTarget.style.background = '#10b981';
                            }
                          }}
                        >
                          {actionLoading === `release-${phase.number}` ? 'Releasing...' : 'Release Payment to Contractor'}
                        </button>
                      </div>
                    )}
                    {/* CB:END release-payment-button */}
                  </div>
                );
              })}
              {(!project.phases || project.phases.length === 0) && (
                <div style={{ textAlign: 'center', padding: '48px 24px' }}>
                  <svg style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>No milestones</h3>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Project milestones will appear here once created.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'audit' && (
          <div style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', marginBottom: '24px' }}>Audit Log</h3>
            {auditLoading ? (
              <p style={{ color: '#6b7280' }}>Loading…</p>
            ) : auditEntries.length === 0 ? (
              <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>No audit entries for this project yet.</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {auditEntries.map((e) => (
                  <li key={e.id} style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb', fontSize: 14 }}>
                    <strong>{e.action}</strong>
                    {e.actorUserId && <span style={{ color: '#6b7280', marginLeft: 8 }}>by {e.actorUserId}</span>}
                    <span style={{ color: '#6b7280', marginLeft: 8 }}>{new Date(e.createdAt).toLocaleString()}</span>
                    {e.payload && typeof e.payload === 'object' && Object.keys(e.payload as object).length > 0 && (
                      <pre style={{ margin: '4px 0 0', fontSize: 12, color: '#4b5563', overflow: 'auto' }}>{JSON.stringify(e.payload)}</pre>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeTab === 'admin-overrides' && (
          <div style={{ padding: '24px', maxWidth: 680 }}>

            {/* ── Header ── */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>Admin Controls</h3>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  background: '#fef3c7', color: '#92400e',
                }}>
                  ⚠ Audited
                </span>
              </div>
              <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                All actions are logged to the audit trail with your admin ID and timestamp.
              </p>
            </div>

            {/* ── Section 1: Send Reminder ── */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Send Reminder</span>
              </div>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px' }}>
                Immediately notify the responsible party that this project needs their attention.
              </p>
              <textarea
                rows={2}
                value={reminderNote}
                onChange={e => setReminderNote(e.target.value)}
                placeholder="Optional note for the audit log (e.g. 'Homeowner unresponsive for 3 days')"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical', marginBottom: 10 }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  disabled={reminderLoading}
                  onClick={async () => {
                    setReminderLoading(true);
                    setReminderResult(null);
                    try {
                      const note = reminderNote.trim() || 'Admin manual trigger';
                      await api.post(`/api/admin/projects/${projectId}/send-reminder`, { reason: note.length >= 5 ? note : 'Admin manual trigger' });
                      setReminderResult({ ok: true, msg: 'Reminder sent successfully' });
                      setReminderNote('');
                    } catch (e: any) {
                      setReminderResult({ ok: false, msg: e?.message ?? 'Failed to send reminder' });
                    } finally {
                      setReminderLoading(false);
                    }
                  }}
                  style={{ padding: '7px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: reminderLoading ? 'not-allowed' : 'pointer', opacity: reminderLoading ? 0.65 : 1 }}
                >
                  {reminderLoading ? 'Sending…' : 'Send Reminder'}
                </button>
                {reminderResult && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: reminderResult.ok ? '#059669' : '#dc2626' }}>
                    {reminderResult.ok ? '✓' : '✗'} {reminderResult.msg}
                  </span>
                )}
              </div>
            </div>

            {/* ── Section 2: Force Status Transition ── */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Force Status Transition</span>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Current: <strong style={{ color: '#111827' }}>{project.status.replace(/_/g, ' ')}</strong>
                </span>
              </div>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px' }}>
                Bypass the state machine and move this project to any status. Requires a justification.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>New Status</label>
                  <select
                    value={transitionStatus}
                    onChange={e => setTransitionStatus(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                  >
                    <option value="">— select target status —</option>
                    {[
                      { value: 'DRAFT',                        label: 'Draft' },
                      { value: 'REQUESTED',                    label: 'Requested' },
                      { value: 'BIDS_COLLECTING',              label: 'Collecting Bids' },
                      { value: 'BIDS_READY',                   label: 'Bids Ready' },
                      { value: 'DECIDING',                     label: 'Deciding' },
                      { value: 'HOMEOWNER_CONTRACT_SIGNING',   label: 'Contract Signing' },
                      { value: 'ESCROW_PENDING',               label: 'Escrow Pending' },
                      { value: 'ACTIVE_P1',                    label: 'Active – Phase 1' },
                      { value: 'ACTIVE_P2',                    label: 'Active – Phase 2' },
                      { value: 'ACTIVE_P3',                    label: 'Active – Phase 3' },
                      { value: 'IN_PROGRESS',                  label: 'In Progress' },
                      { value: 'COMPLETED',                    label: 'Completed' },
                      { value: 'CLOSED',                       label: 'Closed' },
                      { value: 'CANCELLED',                    label: 'Cancelled' },
                      { value: 'DISPUTED',                     label: 'Disputed' },
                    ].filter(o => o.value !== project.status).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>
                    Justification <span style={{ color: '#9ca3af', fontWeight: 400 }}>(min 10 characters)</span>
                  </label>
                  <textarea
                    rows={3}
                    value={transitionJustification}
                    onChange={e => setTransitionJustification(e.target.value)}
                    placeholder="Business reason for this override (e.g. 'Contractor confirmed start date via phone, moving to Active P1')"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }}
                  />
                  <div style={{ fontSize: 11, color: transitionJustification.trim().length < 10 ? '#9ca3af' : '#059669', marginTop: 3 }}>
                    {transitionJustification.trim().length}/10 minimum characters
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    disabled={transitionLoading || !transitionStatus || transitionJustification.trim().length < 10}
                    onClick={async () => {
                      setTransitionLoading(true);
                      setTransitionResult(null);
                      try {
                        await api.post(`/api/admin/projects/${projectId}/override-transition`, {
                          toStatus: transitionStatus,
                          justification: transitionJustification.trim(),
                        });
                        await fetchProject();
                        setTransitionResult({ ok: true, msg: `Moved to ${transitionStatus.replace(/_/g, ' ')}` });
                        setTransitionStatus('');
                        setTransitionJustification('');
                      } catch (e: any) {
                        setTransitionResult({ ok: false, msg: e?.message ?? 'Transition failed' });
                      } finally {
                        setTransitionLoading(false);
                      }
                    }}
                    style={{
                      padding: '7px 16px', background: '#b45309', color: '#fff', border: 'none',
                      borderRadius: 6, fontSize: 13, fontWeight: 600,
                      cursor: (transitionLoading || !transitionStatus || transitionJustification.trim().length < 10) ? 'not-allowed' : 'pointer',
                      opacity: (transitionLoading || !transitionStatus || transitionJustification.trim().length < 10) ? 0.5 : 1,
                    }}
                  >
                    {transitionLoading ? 'Applying…' : 'Apply Transition'}
                  </button>
                  {transitionResult && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: transitionResult.ok ? '#059669' : '#dc2626' }}>
                      {transitionResult.ok ? '✓' : '✗'} {transitionResult.msg}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* ── Section 3: Danger Zone ── */}
            <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 10, padding: '18px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: '#991b1b' }}>Danger Zone</span>
              </div>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px' }}>
                One-click actions that immediately change project state. Use with caution.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {project.status === 'DISPUTED' && (
                  <button
                    disabled={dangerLoading === 'resolve'}
                    onClick={async () => {
                      if (!confirm('Resolve this dispute and move the project back to IN_PROGRESS?')) return;
                      setDangerLoading('resolve');
                      setDangerResult(null);
                      try {
                        await api.post(`/api/admin/projects/${projectId}/override-transition`, {
                          toStatus: 'IN_PROGRESS',
                          justification: 'Admin resolved dispute — returning project to active state',
                        });
                        await fetchProject();
                        setDangerResult({ ok: true, msg: 'Dispute resolved — project is now In Progress' });
                      } catch (e: any) {
                        setDangerResult({ ok: false, msg: e?.message ?? 'Failed' });
                      } finally {
                        setDangerLoading(null);
                      }
                    }}
                    style={{ padding: '7px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: dangerLoading === 'resolve' ? 'not-allowed' : 'pointer', opacity: dangerLoading === 'resolve' ? 0.65 : 1 }}
                  >
                    {dangerLoading === 'resolve' ? 'Resolving…' : 'Resolve Dispute → In Progress'}
                  </button>
                )}
                <button
                  disabled={!!dangerLoading || project.status === 'CANCELLED'}
                  onClick={async () => {
                    if (!confirm(`Cancel this project? This cannot be easily undone.\n\nProject: ${project.address}`)) return;
                    const reason = prompt('Brief reason for cancellation (required):');
                    if (!reason || reason.trim().length < 5) { alert('Cancellation reason required (min 5 chars)'); return; }
                    setDangerLoading('cancel');
                    setDangerResult(null);
                    try {
                      await api.post(`/api/admin/projects/${projectId}/override-transition`, {
                        toStatus: 'CANCELLED',
                        justification: `Admin cancelled: ${reason.trim()}`,
                      });
                      await fetchProject();
                      setDangerResult({ ok: true, msg: 'Project cancelled' });
                    } catch (e: any) {
                      setDangerResult({ ok: false, msg: e?.message ?? 'Failed' });
                    } finally {
                      setDangerLoading(null);
                    }
                  }}
                  style={{
                    padding: '7px 14px', background: '#dc2626', color: '#fff', border: 'none',
                    borderRadius: 6, fontSize: 13, fontWeight: 600,
                    cursor: (!!dangerLoading || project.status === 'CANCELLED') ? 'not-allowed' : 'pointer',
                    opacity: (!!dangerLoading || project.status === 'CANCELLED') ? 0.5 : 1,
                  }}
                >
                  {dangerLoading === 'cancel' ? 'Cancelling…' : 'Cancel Project'}
                </button>
              </div>
              {dangerResult && (
                <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: dangerResult.ok ? '#059669' : '#dc2626' }}>
                  {dangerResult.ok ? '✓' : '✗'} {dangerResult.msg}
                </div>
              )}
            </div>

          </div>
        )}
      </div>

      {/* CB:BEGIN add-email-bid-modal */}
      {projectId && (
        <AdminAddEmailBidModal
          projectId={projectId}
          isOpen={showAddBidModal}
          onClose={() => {
            setShowAddBidModal(false);
            setIsBidFormValid(false);
          }}
          onSuccess={() => {
            fetchProject(); // Refresh project data
            setShowAddBidModal(false);
            setIsBidFormValid(false);
          }}
          verifiedContractors={verifiedContractors}
          onFormValidityChange={setIsBidFormValid}
          submitTrigger={submitBidTrigger}
        />
      )}
      {/* CB:END add-email-bid-modal */}

      {/* CB:BEGIN add-verified-contractor-modal */}
      {projectId && (
        <AddVerifiedContractorModal
          projectId={projectId}
          isOpen={showAddVerifiedContractorModal}
          onClose={() => setShowAddVerifiedContractorModal(false)}
          onSuccess={() => {
            fetchProject(); // Refresh project data to get updated verified contractors
          }}
          existingContractors={verifiedContractors}
        />
      )}
      {/* CB:END add-verified-contractor-modal */}
    </div>
  );
};

export default ProjectCockpit;
// CB:END project-cockpit-comprehensive
